import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { isEntriEnabled } from '../../utils/feature-flags';
import EntriPurchaseButton from './EntriPurchaseButton';
import { ROUTES } from '../../utils/constants';

/**
 * @typedef {import('../../types').CurrentSite} CurrentSite
 */

/**
 * Displays the custom domain setup section when no custom domain is configured
 * Shows single or dual options depending on whether Entri is enabled
 * @param {object} props - Component props
 * @param {CurrentSite} props.currentSite - The current site data
 * @param {string} props.userEmail - The user's email address
 * @param {string} props.domainPurchaseError - Error message from domain purchase attempt
 * @param {Function} props.onDomainPurchase - Handler for successful domain purchase
 * @param {Function} props.onPurchaseError - Handler for domain purchase errors
 * @returns {React.ReactNode} JSX element for the setup custom domain section
 */
function SetupCustomDomainSection({
    currentSite,
    userEmail,
    domainPurchaseError,
    onDomainPurchase,
    onPurchaseError,
}) {
    const entriEnabled = isEntriEnabled(userEmail);

    return (
        <div className="box-domain box-wrap">
            <div className="box">
                <section className="staticfield">
                    <header>
                        <div className="staticfield-label">
                            {entriEnabled ? 'Setup custom domain' : 'Custom Domain'}
                        </div>
                    </header>
                    <div className="domain-setup-options">
                        <div className="domain-option">
                            {entriEnabled && (
                                <>
                                    <div className="staticfield-label domain-option-title">
                                        Use my domain
                                    </div>
                                    <p className="domain-option-description">
                                        Connect a domain you already own and point it to your Ghost
                                        with our quick setup guide.
                                    </p>
                                </>
                            )}
                            <Link
                                className="gh-btn gh-btn-block"
                                data-test-btn="setup-existing-domain"
                                to={ROUTES.CUSTOM_DOMAIN}
                            >
                                Setup existing domain
                            </Link>
                        </div>
                        {entriEnabled && (
                            <>
                                <div className="domain-separator"></div>
                                <div className="domain-option">
                                    <div className="staticfield-label domain-option-title">
                                        Buy a new domain
                                    </div>
                                    <p className="domain-option-description">
                                        Purchase a new domain and we'll handle the setup for you
                                        automatically.
                                    </p>
                                    <EntriPurchaseButton
                                        subdomain={currentSite?.subdomain}
                                        siteId={currentSite?.id}
                                        onError={onPurchaseError}
                                        onDomainPurchased={onDomainPurchase}
                                        className="gh-btn gh-btn-block gh-btn-green"
                                    />
                                    {domainPurchaseError && (
                                        <div className="gh-alert gh-alert-error domain-purchase-error">
                                            {domainPurchaseError}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}

SetupCustomDomainSection.propTypes = {
    currentSite: PropTypes.shape({
        subdomain: PropTypes.string,
        id: PropTypes.string,
    }),
    userEmail: PropTypes.string,
    domainPurchaseError: PropTypes.string,
    onDomainPurchase: PropTypes.func.isRequired,
    onPurchaseError: PropTypes.func.isRequired,
};

export default SetupCustomDomainSection;
